import { useCallback, useEffect, useContext, useMemo } from "react";
import {
	Route,
	Routes,
	Navigate,
	useNavigate,
	useLocation,
} from "react-router-dom";
import UserContext from "./hooks/useUserContext";
import { OrderView, OrdersPage } from "./pages/Orders";
import { ItemsPage } from "./pages/Items";
import { LoginPage } from "./pages/Login";
import { ShipmentItemsPage } from "./pages/ShipmentItems";
import { FulfilledShipmentsPage } from "./pages/FulfilledShipments";
import { ShipmentsPage } from "./pages/Shipments";
import { VendorItemsPage } from "./pages/VendorItems";
import { EditShipmentPage } from "./pages/EditShipment";
import AdvanceShippingNoticePage from "./pages/AdvanceShippingNotices/AdvanceShippingNoticePage";
import { PageNotFound, AuthLayout } from "./components";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { CreatePasswordPage } from "./pages/CreatePassword";
import { handleClientAuthentication } from "./utils/handleClientAuthentication";
import { getQueryParams } from "./utils";
import { getClientUrl } from "./pages/Login/api/LoginApi";
import { useHostName } from "./hooks";
import InventoryRouter from "./pages/Inventory/listing/InventoryRouter";
// Import React Query
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import useHostNameStore from "./stores/useHostNameStore";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 60 * 24, // 24 hours
		},
	},
});

const localStoragePersister = createSyncStoragePersister({
	storage: window.localStorage,
	key: "supply",
});

const InnerApp = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, setUser } = useContext(UserContext);
	const { hostName } = useHostName();
	const { setHostName } = useHostNameStore();

 	// Normalize pathname at the very top - remove trailing slash except for root
	const normalizedPathname = useMemo(() => {
		if (location.pathname.length > 1 && location.pathname.endsWith("/")) {
			return location.pathname.slice(0, -1);
		}
		return location.pathname;
	}, [location.pathname]);

	// Silently update the URL in the browser if it has a trailing slash
	useEffect(() => {
		if (location.pathname !== normalizedPathname) {
			// Use replaceState to update URL without triggering navigation
			window.history.replaceState(
				null,
				"",
				normalizedPathname + location.search + location.hash,
			);
		}
	}, [location.pathname, normalizedPathname, location.search, location.hash]);

	const currentUrl = normalizedPathname + location.search;

	// Move isAuthenticated inside component to access fresh state
	const isAuthenticated = useCallback(() => {
		const token = localStorage.getItem("accessToken");
		const isUserLoggedIn = user.isUserLoggedIn;

		// Add debugging for production

		return !!token && isUserLoggedIn;
	}, [user.isUserLoggedIn]);

	// Helper function to normalize URLs (remove trailing slash except for root)
	const normalizeUrl = (url: string) => {
		if (url.length > 1 && url.endsWith("/")) {
			return url.slice(0, -1);
		}
		return url;
	};

	// Helper function to construct URL with query params
	const buildUrlWithParams = (baseUrl: any, path: string, params: string) => {
		const normalizedBase = normalizeUrl(baseUrl);
		const normalizedPath = path.startsWith("/") ? path : `/${path}`;
		const fullPath = `${normalizedBase}${normalizedPath}`;
		return params ? `${fullPath}?${params}` : fullPath;
	};

	/* ---------------------------------------------------------------------------------------------- */
	/*                                  CLIENT AUTHENTICATION HANDLER START                               */
	/* ---------------------------------------------------------------------------------------------- */

	useEffect(() => {
		const handleAuthentication = async () => {
			const queryParams = getQueryParams();
			setHostName(hostName);
			// Handle customerIdentity parameter
			if (queryParams["customerIdentity"] !== undefined) {
				try {
					const url = await getClientUrl(
						queryParams["customerIdentity"],
					);

					// Check authentication status
					const token = localStorage.getItem("accessToken");

					if (token) {
						// User is authenticated, redirect to shipment-items with internalId
						const urlWithParams = buildUrlWithParams(
							url,
							"shipment-items",
							`internalId=${queryParams["internalId"]}`,
						);
						window.location.href = urlWithParams;
					} else {
						// User is not authenticated, redirect to login with internalId
						const urlWithParams = buildUrlWithParams(
							url,
							"login",
							`internalId=${queryParams["internalId"]}`,
						);
						window.location.href = urlWithParams;
					}
				} catch (error) {
					// Handle error - maybe redirect to login
					navigate("/login");
				}
				return; // Exit early if customerIdentity is handled
			}

			// Handle SAML authentication
			if (queryParams["saml"] !== undefined) {
				await handleClientAuthentication(
					user,
					setUser,
					navigate,
					currentUrl,
				);
				return;
			}

			// Handle regular authentication check for protected routes
			const publicPaths = [
				"/login",
				"/forgot-password",
				"/create-password",
			];
			// Always use the normalized pathname for route checking
			const isPublicPath = publicPaths.includes(normalizedPathname);

			if (!isPublicPath && !user.isUserLoggedIn) {
				await handleClientAuthentication(
					user,
					setUser,
					navigate,
					currentUrl,
				);
			}
		};

		handleAuthentication();
	}, [normalizedPathname, location.search]); // Use normalized pathname

	/* ---------------------------------------------------------------------------------------------- */
	/*                                    SET TABLE TO FIRST MOUNT                                    */
	/* ---------------------------------------------------------------------------------------------- */
	useEffect(() => {
		sessionStorage.setItem("table_first_mount", "true");
	}, []);

	/* ---------------------------------------------------------------------------------------------- */
	/*                                  CLIENT AUTHENTICATION HANDLER END                              */
	/* ---------------------------------------------------------------------------------------------- */

	// Create a virtual location object with normalized pathname
	const virtualLocation = useMemo(
		() => ({
			...location,
			pathname: normalizedPathname,
		}),
		[location, normalizedPathname],
	);

	return (
		<div className="App app-body m-0 relative overflow-hidden">
			<PersistQueryClientProvider
				client={queryClient}
				persistOptions={{ persister: localStoragePersister }}
			>
				<Routes location={virtualLocation}>
					<Route
						element={
							<AuthLayout
								showFooter={
									user.isUserLoggedIn && isAuthenticated()
								}
							/>
						}
					>
						{/* Publicly accessible routes */}
						<Route path="/login" element={<LoginPage />} />
						<Route
							path="/forgot-password"
							element={<ForgotPasswordPage />}
						/>
						<Route
							path="/create-password"
							element={<CreatePasswordPage />}
						/>

						{/* Private routes, accessible only to logged-in users */}
						{user.isUserLoggedIn ? (
							<>
								<Route
									path="/order-view/:uuid"
									element={<OrderView />}
								/>
								<Route path="/items" element={<ItemsPage />} />
								<Route
									path="/vendor-items"
									element={<VendorItemsPage />}
								/>
								<Route
									path="/fulfilled-shipments"
									element={<FulfilledShipmentsPage />}
								/>
								<Route
									path="/shipments"
									element={<ShipmentsPage />}
								/>
								<Route
									path="/shipment-items"
									element={<ShipmentItemsPage />}
								/>
								<Route
									path="/edit-shipment"
									element={<EditShipmentPage />}
								/>
								<Route
									path="/advance-shipping-notice"
									element={<AdvanceShippingNoticePage />}
								/>
								<Route
									path="/inventory"
									element={<InventoryRouter />}
								/>
								<Route
									path="/inventory_items"
									element={<InventoryRouter />}
								/>
								<Route
									path="/"
									element={
										localStorage.getItem(
											"officeDepotUser",
										) === "true" ? (
											<Navigate
												replace
												to="/vendor-items"
											/>
										) : (
											<OrdersPage />
										)
									}
								/>
								<Route path="/*" element={<PageNotFound />} />
							</>
						) : (
							""
						)}
					</Route>
				</Routes>
			</PersistQueryClientProvider>
		</div>
	);
};

export default InnerApp;
